import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

import { getShopProductUrl } from '../../helpers/helpers';
import { formatDate, getAgentSummary, getLambdaSummary } from '../../helpers/url_data_agent_evaluations_helpers';
import {
  canConfirmAutomaticMatch,
  formatConfidence,
  formatPrice,
  getApplyDecisionSummary,
  getLiveMatchActions,
  getLiveMatchAutomationReason,
  getLiveMatchCandidates,
  getLiveMatchConfidence,
  getLiveMatchConfidenceBand,
  getLiveMatchConfirmation,
  getLiveMatchProduct,
  getLiveMatchReason,
  getLiveMatchReviewReason,
  getLiveMatchRunFacts,
  getLiveMatchUnresolvedChecks,
  getProductAdminUrl,
  getUrlDataAdminUrl,
  isCatalogBrandCreation,
  isLiveMatchBrandMismatch,
  isProductCreation
} from '../../helpers/url_data_agent_live_matches_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import PreviewThumb from './PreviewThumb';
import SafeImage from './SafeImage';

import './LiveMatchDetailDrawer.scss';

/**
 * The full story of one row: both products large, the agent's reasoning, the other candidates it
 * weighed, and the run facts. Renders from the list row at once; the detail payload fills in the rest.
 */
const LiveMatchDetailDrawer = ({ row, evaluation, applyControl, isLoading, isBusy, onApply, onConfirm, onCorrect, onPickCandidate, onSendToHuman, onClose }) => {
  const data = evaluation || row;
  const source = getLambdaSummary(data);
  const agent = getAgentSummary(data);
  const product = getLiveMatchProduct(data);
  const isCreation = isProductCreation(data);
  const reviewReason = getLiveMatchReviewReason(data);
  const automationReason = getLiveMatchAutomationReason(data, applyControl);
  const unresolvedChecks = getLiveMatchUnresolvedChecks(data);
  const decision = getApplyDecisionSummary(data, applyControl);
  const confidence = getLiveMatchConfidence(data);
  const band = getLiveMatchConfidenceBand(confidence);
  const brandMismatch = isLiveMatchBrandMismatch(data);
  const sourceUrl = getUrlDataLink(data);
  const urlDataUrl = getUrlDataAdminUrl(data);
  const sourceTitle = source.productName || data.urlStem || 'Unknown page';
  const sourcePrice = formatPrice(data.productPrice, data.productCurrency);
  const productTitle = product?.title || (data.humanAction ? 'No product preview available' : agent.productName || '—');
  const productBrand = product?.brand || (!data.humanAction && (data.currentApplyPolicy?.productToCreateBrand || agent.brandName)) || null;
  const productPrice = product ? formatPrice(product.fallbackPrice, product.fallbackPriceCurrency) : null;
  const reason = getLiveMatchReason(data);
  const candidates = getLiveMatchCandidates(evaluation);
  const facts = getLiveMatchRunFacts(evaluation);
  const confirmation = getLiveMatchConfirmation(evaluation);

  // Actions
  const handlers = { approve: onApply, correct: onCorrect, send_back: onSendToHuman, undo: onSendToHuman };
  const actions = getLiveMatchActions(data);
  const canConfirm = onConfirm && canConfirmAutomaticMatch(evaluation);

  return (
    <div className='live-match-detail-drawer' onClick={onClose}>
      <aside className='lmd-panel' role='dialog' aria-label='Match details' onClick={e => e.stopPropagation()}>
        <header className='lmd-head'>
          <span className='lmd-state'>{decision.label}</span>
          <span className={cn('lmd-conf', band && `band-${band}`)}>{formatConfidence(confidence)}</span>
          <span className='lmd-domain'>{data.domain}</span>
          <div className='lmd-actions'>
            {canConfirm && (
              <button type='button' className='se-btn' disabled={isBusy} onClick={onConfirm}>
                Confirm match
              </button>
            )}
            {actions.map(action => (
              <button key={action.key} type='button' className={cn('se-btn', { primary: action.primary })} disabled={isBusy} onClick={handlers[action.key]}>
                {action.display}
              </button>
            ))}
          </div>
          <button type='button' className='lmd-close' aria-label='Close details' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>

        <div className='lmd-compare'>
          <figure className='lmd-side'>
            <div className='lmd-image'>
              <SafeImage source={source.primaryImageUrl} placeholderText='No page image' alt='' />
            </div>
            <figcaption>
              <div className='lmd-title' title={sourceTitle}>
                {sourceUrl ? (
                  <a href={sourceUrl} target='_blank' rel='noopener noreferrer'>
                    {sourceTitle}
                  </a>
                ) : (
                  sourceTitle
                )}
              </div>
              <div className='lmd-meta'>
                <span>{data.domain || 'page'}</span>
                <span className={cn({ 'lmd-brand-mismatch': brandMismatch })}>{source.brandName || '—'}</span>
                {sourcePrice && <span>{sourcePrice}</span>}
                {urlDataUrl && (
                  <a href={urlDataUrl} target='_blank' rel='noopener noreferrer'>
                    ↗ URL data
                  </a>
                )}
              </div>
            </figcaption>
          </figure>
          <span className={cn('lmd-glyph', { mismatch: brandMismatch })} aria-hidden='true'>
            {brandMismatch ? '≠' : '⇄'}
          </span>
          <figure className='lmd-side'>
            <div className='lmd-image'>
              <SafeImage source={product?.image || (data.humanAction ? null : agent.primaryImageUrl)} placeholderText={isCreation ? 'No product image' : 'No catalog image'} alt='' />
            </div>
            <figcaption>
              <div className='lmd-title' title={productTitle}>
                {product ? (
                  <a href={getShopProductUrl(product.id)} target='_blank' rel='noopener noreferrer'>
                    {productTitle}
                  </a>
                ) : (
                  productTitle
                )}
              </div>
              <div className='lmd-meta'>
                <span className={cn({ 'lmd-brand-mismatch': brandMismatch })}>{productBrand || '—'}</span>
                {(product?.category?.name || (isCreation && agent.categoryName)) && <span>{product?.category?.name || agent.categoryName}</span>}
                {isCreation && !product && <span>New product preview</span>}
                {productPrice && <span>{productPrice}</span>}
                {product && (
                  <a className='mono' href={getProductAdminUrl(product.id)} target='_blank' rel='noopener noreferrer'>
                    #{product.id}
                  </a>
                )}
              </div>
            </figcaption>
          </figure>
        </div>

        {(automationReason || unresolvedChecks.length > 0) && (
          <section className='lmd-section'>
            <h3>{data.applyDecision === 'proposed' ? 'Why this is waiting' : 'Unresolved checks from this run'}</h3>
            {automationReason && <p>{automationReason}.</p>}
            {unresolvedChecks.map(check => <p key={check}>{check}</p>)}
            {reviewReason && <p>Choose an existing product if you can verify it, or send this link to the catalog team.</p>}
          </section>
        )}

        {confirmation && (
          <section className='lmd-section'>
            <h3>Match confirmed</h3>
            <p>
              {confirmation.reviewer?.name || 'A reviewer'} confirmed this run’s match
              {confirmation.correct_Product_id && <> to product <a href={getProductAdminUrl(confirmation.correct_Product_id)}>#{confirmation.correct_Product_id}</a></>}
              {confirmation.createdAt && ` on ${formatDate(confirmation.createdAt)}`}.
            </p>
          </section>
        )}

        {isCatalogBrandCreation(data) && (
          <section className='lmd-section'>
            <h3>{data.createdAllBrandId ? 'Catalog brand created' : 'New catalog brand'}</h3>
            <p>{data.createdAllBrandId ? `This run created catalog brand “${agent.brandName}” (#${data.createdAllBrandId}).` : `Approval also creates the catalog brand “${agent.brandName}”.`} Undoing the match keeps the brand in the catalog.</p>
          </section>
        )}

        <section className='lmd-section'>
          <h3>Agent reasoning</h3>
          <p>{reason || 'The agent left no reasoning on this run.'}</p>
        </section>

        {isLoading ? (
          <div className='lmd-loading'>Loading run details…</div>
        ) : (
          <>
            {candidates.length > 0 && (
              <section className='lmd-section'>
                <h3>APM shortlist</h3>
                <div className='lmd-candidates'>
                  {candidates.map(candidate => (
                    <div key={candidate.Product_id} className={cn('lmd-candidate', { chosen: candidate.chosen })}>
                      <PreviewThumb source={candidate.image} title={candidate.title} subtitle={candidate.brand} large />
                      <span className='candidate-score'>
                        {candidate.score ?? '—'}
                        {candidate.chosen && <em>picked</em>}
                      </span>
                      <span className='candidate-title' title={candidate.title}>
                        {candidate.title}
                      </span>
                      <span className='candidate-brand'>{[candidate.brand, candidate.isUsed && 'used'].filter(Boolean).join(' · ')}</span>
                      <span className='candidate-links'>
                        <a href={getShopProductUrl(candidate.Product_id)} target='_blank' rel='noopener noreferrer'>
                          Shop ↗
                        </a>
                        <a className='mono' href={getProductAdminUrl(candidate.Product_id)} target='_blank' rel='noopener noreferrer'>
                          #{candidate.Product_id}
                        </a>
                      </span>
                      {actions.some(action => action.key === 'correct') && (
                        <button type='button' className='se-btn' disabled={isBusy} onClick={() => onPickCandidate({ id: candidate.Product_id })}>
                          Use this product
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
            {facts.length > 0 && (
              <footer className='lmd-footer'>
                {facts.map(fact => (
                  <span key={fact.label}>
                    <b>{fact.label}</b> {fact.value}
                  </span>
                ))}
              </footer>
            )}
          </>
        )}
      </aside>
    </div>
  );
};

LiveMatchDetailDrawer.propTypes = {
  row: PropTypes.object.isRequired,
  evaluation: PropTypes.object,
  applyControl: PropTypes.object,
  isLoading: PropTypes.bool,
  isBusy: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onCorrect: PropTypes.func.isRequired,
  onPickCandidate: PropTypes.func.isRequired,
  onSendToHuman: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default LiveMatchDetailDrawer;
